import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

function TrainingAdmin() {
  const [serverGroups, setServerGroups] = useState(
    Array.from({ length: 10 }, (_, i) => ({
      name: `Group ${String.fromCharCode(65 + i)}`,
      serverIP: '',
      region: 'eastus', // Default to East US
      vmConnections: Array(10).fill().map((_, index) => ({
        nickname: `VM ${index + 1}`,
        connection: ''
      }))
    }))
  );
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  const azureRegions = [
    { value: 'eastus', label: 'East US' },
    { value: 'eastus2', label: 'East US 2' },
    { value: 'westus', label: 'West US' },
    { value: 'westus2', label: 'West US 2' },
    { value: 'centralus', label: 'Central US' },
    { value: 'northcentralus', label: 'North Central US' },
    { value: 'southcentralus', label: 'South Central US' },
    { value: 'westcentralus', label: 'West Central US' },
    { value: 'northeurope', label: 'North Europe' },
    { value: 'westeurope', label: 'West Europe' },
    { value: 'uksouth', label: 'UK South' },
    { value: 'ukwest', label: 'UK West' },
    { value: 'southeastasia', label: 'Southeast Asia' },
    { value: 'eastasia', label: 'East Asia' },
    { value: 'australiaeast', label: 'Australia East' },
    { value: 'australiasoutheast', label: 'Australia Southeast' }
  ];

  // Load existing configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'training-config', 'student-settings');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setServerGroups(docSnap.data().serverGroups || Array.from({ length: 10 }, (_, i) => ({
            name: `Group ${String.fromCharCode(65 + i)}`,
            serverIP: '',
            region: 'eastus',
            vmConnections: Array(10).fill().map((_, index) => ({
              nickname: `VM ${index + 1}`,
              connection: ''
            }))
          })));
        }
        setIsLoading(false);
      } catch (error) {
        setMessage('Error loading configuration: ' + error.message);
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Helper function to check if string is valid IP
  const isValidIP = (ip) => {
    const parts = ip.split('.');
    if (parts.length !== 4) return false;
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  };

  // Helper function to replace IP in connection string
  const replaceIPInConnection = (connectionString, newIP) => {
    const urlIPPattern = /(https?:\/\/)\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}([:\/])/;
    return connectionString.replace(urlIPPattern, `$1${newIP}$2`);
  };

  // Handle server IP change for a group
  const handleServerIPChange = (groupIndex, newIP) => {
    setServerGroups(prev => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        serverIP: newIP,
        vmConnections: isValidIP(newIP) 
          ? Array(10).fill().map((_, index) => ({
              nickname: `VM ${index + 1}`,
              connection: `http://${newIP}:8080/training/#/client/${index + 1}`
            }))
          : newGroups[groupIndex].vmConnections
      };
      return newGroups;
    });
  };

  // Handle region change for a group
  const handleRegionChange = (groupIndex, newRegion) => {
    setServerGroups(prev => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        region: newRegion
      };
      return newGroups;
    });
  };

  // Handle VM connection changes
  const handleVMConnectionChange = (groupIndex, vmIndex, field, value) => {
    setServerGroups(prev => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        vmConnections: newGroups[groupIndex].vmConnections.map((conn, i) => 
          i === vmIndex ? { ...conn, [field]: value } : conn
        )
      };
      return newGroups;
    });
  };

  // Reset nicknames for a group
  const handleResetNicknamesClick = (groupIndex) => {
    setShowResetConfirmation(true);
    setServerGroups(prev => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        vmConnections: newGroups[groupIndex].vmConnections.map((_, index) => ({
          ...newGroups[groupIndex].vmConnections[index],
          nickname: `VM ${index + 1}`
        }))
      };
      return newGroups;
    });
  };

  // Save configuration to Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await setDoc(doc(db, 'training-config', 'student-settings'), { serverGroups });
      setMessage('Configuration saved successfully!');
    } catch (error) {
      setMessage('Error saving configuration: ' + error.message);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="training-admin">
          <h2>Training Administration</h2>
          {message && <p className={message.includes('Error') ? 'error' : 'success'}>{message}</p>}
          
          <form onSubmit={handleSubmit} className="admin-form">
            {serverGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="form-section">
                <h3>{group.name}</h3>
                
                <div className="form-group">
                  <label htmlFor={`server-${groupIndex}`}>Server IP:</label>
                  <input
                    id={`server-${groupIndex}`}
                    type="text"
                    value={group.serverIP}
                    onChange={(e) => handleServerIPChange(groupIndex, e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`region-${groupIndex}`}>Azure Region:</label>
                  <select
                    id={`region-${groupIndex}`}
                    value={group.region}
                    onChange={(e) => handleRegionChange(groupIndex, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc'
                    }}
                  >
                    {azureRegions.map(region => (
                      <option key={region.value} value={region.value}>
                        {region.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <button 
                    type="button" 
                    onClick={() => handleResetNicknamesClick(groupIndex)}
                    className="reset-btn"
                  >
                    Reset Nicknames
                  </button>
                </div>

                <div className="vm-connections-list">
                  {group.vmConnections.map((conn, vmIndex) => (
                    <div key={vmIndex} className="vm-connection-entry" style={{
                      display: 'flex',
                      gap: '10px',
                      marginBottom: '10px',
                      alignItems: 'center',
                      width: '100%'
                    }}>
                      <div className="vm-connection-fields" style={{
                        display: 'flex',
                        gap: '10px',
                        flex: '1'
                      }}>
                        <input
                          type="text"
                          value={conn.nickname}
                          onChange={(e) => handleVMConnectionChange(groupIndex, vmIndex, 'nickname', e.target.value)}
                          placeholder={`VM Nickname #${vmIndex + 1}`}
                          className="nickname-input"
                          style={{ width: '30%' }}
                        />
                        <input
                          type="text"
                          value={conn.connection}
                          onChange={(e) => handleVMConnectionChange(groupIndex, vmIndex, 'connection', e.target.value)}
                          placeholder={`VM Connection String #${vmIndex + 1}`}
                          className="connection-input"
                          style={{ flex: '1' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="form-actions">
              <button type="submit" className="save-btn">
                Save Configuration
              </button>
            </div>
          </form>
        </div>

        {showResetConfirmation && (
          <div className="confirmation-dialog" style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '5px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            zIndex: 1001,
            minWidth: '300px'
          }}>
            <div className="confirmation-content">
              <p style={{ marginTop: 0 }}>Nicknames have been reset to default values.</p>
              <div className="confirmation-actions" style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
                marginTop: '20px'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowResetConfirmation(false)}
                  className="confirm-btn"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {showResetConfirmation && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
          }} onClick={() => setShowResetConfirmation(false)} />
        )}
      </header>
    </div>
  );
}

export default TrainingAdmin; 